/**
 * Phone input with country code selection and validation
 */

import React, { useState, useEffect, useRef } from 'react';
import { Phone, ChevronDown, AlertCircle, Check, Search } from 'lucide-react';
import { COUNTRIES, validatePhone, formatPhoneNumber, ValidationError } from '@/utils/validation';

interface PhoneInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (countryCode: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  helpText?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  value,
  onChange,
  onCountryChange,
  defaultCountry = 'US',
  placeholder,
  disabled = false,
  required = false,
  className = '',
  helpText
}) => {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    // Try to detect country from existing phone number
    if (value) {
      for (const country of COUNTRIES) {
        if (value.startsWith(country.phoneCode)) {
          return country.code;
        }
      }
    }
    return defaultCountry;
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<ValidationError | null>(null);
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const country = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0];

  // Auto-add country code when user starts typing (only if no country code present)
  useEffect(() => {
    if (value && !value.startsWith('+') && country) {
      // If user has typed something but no country code, add it
      const cleanValue = value.replace(/^\+\d{1,4}\s?/, '');
      if (cleanValue && cleanValue !== country.phoneCode) {
        onChange(country.phoneCode + ' ' + cleanValue);
      }
    }
  }, [selectedCountry, country]);

  // Validation
  useEffect(() => {
    if (touched) {
      const validationError = validatePhone(value, selectedCountry);
      setError(validationError);
      setIsValid(!validationError && value.trim() !== '' && value !== country.phoneCode + ' ');
    }
  }, [value, selectedCountry, touched, country.phoneCode]);

  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phoneCode.includes(searchQuery)
  );

  // Handle country selection
  const handleCountrySelect = (countryCode: string) => {
    const newCountry = COUNTRIES.find(c => c.code === countryCode);
    if (!newCountry) return;

    setSelectedCountry(countryCode);
    setIsDropdownOpen(false);
    setSearchQuery('');
    onCountryChange?.(countryCode);

    // Auto-add country code and format existing number
    if (value) {
      // Remove any existing country code
      let cleanNumber = value.replace(/^\+\d{1,4}\s?/, '');
      // Remove any non-digit characters except spaces and dashes
      cleanNumber = cleanNumber.replace(/[^\d\s\-\(\)]/g, '');
      // Add new country code
      const newValue = cleanNumber ? `${newCountry.phoneCode} ${cleanNumber}` : newCountry.phoneCode + ' ';
      onChange(newValue);
    } else {
      // Start with country code
      onChange(newCountry.phoneCode + ' ');
    }
  };

  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Ensure country code is always present
    if (!newValue.startsWith(country.phoneCode)) {
      // If user deleted country code, restore it
      if (newValue.length < country.phoneCode.length) {
        newValue = country.phoneCode + ' ';
      } else {
        // If user typed something that doesn't start with country code, prepend it
        const withoutCountryCode = newValue.replace(/^\+\d{1,4}\s?/, '');
        newValue = `${country.phoneCode} ${withoutCountryCode}`;
      }
    }

    // Remove any non-phone characters except allowed ones
    newValue = newValue.replace(/[^\d\s\-\(\)\+]/g, '');

    // Limit length
    if (newValue.length > 20) return;

    onChange(newValue);
  };

  // Handle input blur
  const handleBlur = () => {
    setTouched(true);
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputClasses = `
    flex-1 px-3 py-2 rounded-r-lg bg-white dark:bg-gray-900 text-sm border-l-0 border
    focus:outline-none focus:ring-1 transition-all duration-200
    ${error && touched
      ? 'border-red-300 dark:border-red-700 focus:ring-red-300 dark:focus:ring-red-700'
      : isValid && touched
      ? 'border-green-300 dark:border-green-700 focus:ring-green-300 dark:focus:ring-green-700'
      : 'border-gray-200 dark:border-gray-800 focus:ring-gray-300 dark:focus:ring-gray-700'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  const countryButtonClasses = `
    flex items-center gap-2 px-3 py-2 rounded-l-lg bg-white dark:bg-gray-900 border
    border-r-0 focus:outline-none focus:ring-1 transition-all duration-200 cursor-pointer
    ${error && touched
      ? 'border-red-300 dark:border-red-700 focus:ring-red-300 dark:focus:ring-red-700'
      : isValid && touched
      ? 'border-green-300 dark:border-green-700 focus:ring-green-300 dark:focus:ring-green-700'
      : 'border-gray-200 dark:border-gray-800 focus:ring-gray-300 dark:focus:ring-gray-700'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}
  `.trim();

  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    // Generate example based on country (without country code since it's auto-added)
    switch (selectedCountry) {
      case 'US':
      case 'CA':
        return '555 123 4567';
      case 'GB':
        return '7700 900123';
      case 'AU':
        return '4 1234 5678';
      case 'DE':
        return '30 12345678';
      case 'FR':
        return '1 23 45 67 89';
      case 'IN':
        return '98765 43210';
      case 'JP':
        return '90 1234 5678';
      case 'CN':
        return '138 0013 8000';
      default:
        return 'Enter your number';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <div className="flex">
          {/* Country selector */}
          <button
            type="button"
            className={countryButtonClasses}
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
          >
            <span className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono">
              {country.code}
            </span>
            <span className="text-sm font-medium">{country.phoneCode}</span>
            <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Phone input */}
          <div className="relative flex-1">
            <input
              type="tel"
              className={inputClasses}
              value={value}
              onChange={handlePhoneChange}
              onBlur={handleBlur}
              placeholder={getPlaceholder()}
              disabled={disabled}
            />

            {/* Validation icons */}
            {touched && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {error ? (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                ) : isValid ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Country dropdown */}
        {isDropdownOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-20 left-0 mt-1 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg max-h-80 overflow-hidden"
          >
            {/* Search */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
                  placeholder="Search countries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Country list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredCountries.map((c) => (
                <div
                  key={c.code}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm"
                  onClick={() => handleCountrySelect(c.code)}
                >
                  <span className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono min-w-[2.5rem] text-center">
                    {c.code}
                  </span>
                  <span className="font-medium min-w-[3rem]">{c.phoneCode}</span>
                  <span className="flex-1">{c.name}</span>
                  {c.code === selectedCountry && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                </div>
              ))}

              {filteredCountries.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && touched && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error.message}
        </p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}

      {/* Format hint */}
      {!error && !helpText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Country code {country.phoneCode} is automatically added. Just enter your local number.
        </p>
      )}
    </div>
  );
};
